import React, { useEffect, useState } from 'react';
import { fetchBriefIndex, fetchBrief, rememberBrief } from '../briefs/briefApi';
import { wingOf } from '../briefs/paths';
import { fetchSyllabusIndex, fetchSyllabus, rememberSyllabus } from '../discuss/discussApi';
import { isSchool } from '../programs';
import { readMirror } from '../serverApi';
import { briefRows, questionCount, syllabusRows, weekGroups } from './stats';
import { SERVER_STATS } from './serverStats';

/*
 * What a tab asks a student to learn, counted from its own data and shown in that tab's panel
 * on the About page: EpStats under EPs/Limits, BriefStats under Briefs, SyllabusStats under
 * Discussion Items.
 *
 * platforms: [{ aircraft, eps: { eps, steps, words, nwcs }, limits }], one per aircraft the
 *   school flies. Advanced covers several, and each has its own EPs, limits and briefs, so a
 *   school with more than one (or passing `byAircraft`, for one expecting more) gets a group
 *   per aircraft, titled with it.
 *
 * The briefs and syllabi are on the mirror. Each is fetched once per school per session and
 * put in the caches their own tabs read, so opening one afterwards costs nothing.
 *
 * These numbers change only when somebody publishes, which is rare, and waiting on a fetch of
 * every syllabus document to show six of them made the page look broken on arrival. So they are
 * SEEDED from a snapshot in the repo (serverStats.js, written by tools/about-stats.js) and
 * painted at once, and the read still happens behind them and corrects anything a publish has
 * changed since. A school the snapshot does not know still shows a dash until its read lands,
 * which is what a newly added one does.
 */

const format = (n) => (n == null ? '–' : n.toLocaleString('en-US'));

async function loadBriefs(school) {
  const index = await fetchBriefIndex();
  const rows = ((index && index.briefs) || []).filter((b) => isSchool(b, school));
  const records = (await Promise.all(rows.map((b) => fetchBrief(b.id)))).filter(Boolean);
  records.forEach(rememberBrief);
  return records;
}

async function loadSyllabi(school) {
  const index = await fetchSyllabusIndex();
  const rows = ((index && index.syllabi) || []).filter((s) => isSchool(s, school));
  const records = (await Promise.all(rows.map((s) => fetchSyllabus(s.id)))).filter(Boolean);
  records.forEach(rememberSyllabus);
  return records;
}

// Only NIFE has a question bank, so there is no school in the key.
async function loadQuestions() {
  const [approved, sections] = await Promise.all([
    readMirror('questions/nife/approved.json'),
    readMirror('questions/nife/sections.json').catch(() => null),
  ]);
  return { questions: approved && approved.questions, doc: sections && sections.doc };
}

const deriveQuestions = (records) => questionCount(records.questions, records.doc);

// One read per school and kind, shared: the same numbers are mounted twice, in the panel and
// under the blurb a phone shows instead of it. A failed read settles on null and changes
// nothing, so the seeded figures stand, and is forgotten so the next mount tries again.
const loads = new Map();

// `derive` turns the records into the figures a panel shows, and is the same function the
// snapshot was written with, so a seeded number and a fetched one are computed one way.
//
// `source` is what gets read and `kind` is what gets shown, and they are separate because the
// syllabi are read once and shown two ways — as course lengths on the landing page and as
// discuss-item counts on the About page. Keying the read by `kind` would fetch every syllabus
// document twice.
function useFigures(source, kind, load, derive, school) {
  const key = `${source}:${school}`;
  const [state, setState] = useState(() => (SERVER_STATS[school] || {})[kind]);
  useEffect(() => {
    setState((SERVER_STATS[school] || {})[kind]);
    if (!loads.has(key)) loads.set(key, load(school).catch(() => { loads.delete(key); return null; }));
    let live = true;
    loads.get(key).then((records) => { if (live && records !== null) setState(derive(records)); });
    return () => { live = false; };
  }, [key, load, derive, school, kind]);
  return state;
}

// A stat whose value is `undefined` is one this school does not have and is left out; `null`
// is one still loading and shows a dash. `one` is the label for a count of exactly one.
function Group({ title, stats, caption }) {
  const shown = stats.filter((s) => s.value !== undefined);
  return (
    <section className="about-stats-group">
      {title && <h3 className="about-stats-title">{title}</h3>}
      <dl className="about-stats-row">
        {shown.map((s) => (
          <div key={s.label} className="about-stat">
            <dt>{s.value === 1 && s.one ? s.one : s.label}</dt>
            <dd>{format(s.value)}</dd>
          </div>
        ))}
      </dl>
      {caption && <p className="about-stats-caption">{caption}</p>}
    </section>
  );
}

const perAircraft = (platforms, byAircraft) => byAircraft || platforms.length > 1;

export function EpStats({ platforms, byAircraft = false }) {
  const titled = perAircraft(platforms, byAircraft);
  return (
    <div className="about-stats">
      {platforms.map(({ aircraft, eps, limits }) => (
        <Group
          key={aircraft}
          title={titled ? aircraft : null}
          stats={[
            { label: 'EPs', one: 'EP', value: eps.eps },
            { label: 'steps', one: 'step', value: eps.steps },
            { label: 'words', one: 'word', value: eps.words },
            // A sheet with no NWCs (NIFE's) shows no NWC count, rather than a zero.
            { label: 'NWCs', one: 'NWC', value: eps.nwcs || undefined },
            { label: 'limits', one: 'limit', value: limits },
          ]}
        />
      ))}
    </div>
  );
}

// A school whose briefs come from two or more wings' guides (Primary: TW-4 and TW-5) shows one
// group per wing, side by side like its syllabi, as the Briefs tab's dropdown splits them. A
// brief that names no wing is shown under every wing, as that tab does.
export function BriefStats({ school, platforms = [], byAircraft = false }) {
  const briefs = useFigures('briefs', 'briefs', loadBriefs, briefRows, school);
  const titled = perAircraft(platforms, byAircraft);
  const units = titled || !briefs ? [] : [...new Set(briefs.map((b) => wingOf(b.unit)).filter(Boolean))].sort();
  const groups = units.length > 1
    ? units.map((unit) => ({ key: unit, title: unit, test: (b) => !b.unit || wingOf(b.unit) === unit }))
    : (titled ? platforms.map((p) => p.aircraft) : [null])
      .map((aircraft) => ({ key: aircraft || 'all', title: aircraft, test: (b) => !aircraft || b.aircraft === aircraft }));
  return (
    <div className={units.length > 1 ? 'about-stats about-stats--across' : 'about-stats'}>
      {groups.map(({ key, title, test }) => {
        const mine = !briefs ? null : briefs.filter(test);
        if (mine && titled && !mine.length) return null;
        return (
          <Group
            key={key}
            title={title}
            stats={[
              { label: 'briefs', one: 'brief', value: mine && mine.length },
            ]}
          />
        );
      })}
    </div>
  );
}

// Each of the school's syllabi side by side (Primary has Delta and Echo), titled with its name.
// The course length, hours and events by type are on the syllabus's own All Events page.
export function SyllabusStats({ school }) {
  const syllabi = useFigures('syllabi', 'syllabi', loadSyllabi, syllabusRows, school);
  return (
    <div className="about-stats about-stats--across">
      {(syllabi || [null]).map((row) => (
        <Group
          key={row ? row.id : 'loading'}
          title={row ? row.name : 'Syllabus'}
          stats={[{ label: 'discuss items', one: 'discuss item', value: row ? row.discussItems : null }]}
        />
      ))}
    </div>
  );
}

// How many questions the Questions tab has live, for the About page's words about it: the
// snapshot at once, then the mirror. Null (say no number) if neither has one.
export function useQuestionCount(school = 'NIFE') {
  const n = useFigures('questions', 'questions', loadQuestions, deriveQuestions, school);
  return n == null ? null : n;
}

export function useCourseWeeks(school, wanted = true) {
  return useFigures('syllabi', 'weeks', wanted ? loadSyllabi : NONE, weekGroups, wanted ? school : `-${school}`);
}

const NONE = () => Promise.resolve([]);
